// Dokumente › Postausgang: alle per E-POST verschickten Briefe an einem Ort – Stand, Fehler, Serienbriefe gebündelt,
// wartender Sammelversand (sofort verschicken / abbrechen), Monatsübersicht je Objekt, „Neuer Brief“.
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowClockwise, Buildings, PencilSimple, CaretDown, CaretRight, FileText, FilePdf, FolderOpen, MagnifyingGlass, PaperPlaneTilt, SpinnerGap, Stop, UserCircle, WarningCircle, type Icon } from '@phosphor-icons/react';
import { Chip, ImmoButton, SchalterZeile } from '../../../../components/immobilien/ui';
import { Segment } from '../../../../components/ui/Segment';
import { PostLogo } from '../../../../components/epost/PostLogo';
import { useMobilModus } from '../../../../lib/immobilien/mobil';
import { aktualisiereBriefStatus, ladeTestbrief, statusText } from '../../../../lib/epost/epost';
import {
  EMPF_ART, ablagePfad, buendle, empfaengerPfad, filtere, objektPfad, quelle, ladePostausgang, monatName, monatsUebersicht, sammelAbbrechen, sammelFreigeben, stand, wartetSammel, zaehleStaende,
  type AusgangBrief, type StandFilter,
} from '../../../../lib/epost/postausgang';
import { PostVersandDialog } from './PostVersandDialog';
import { SchreibenLaeufe } from '../../schreiben/SchreibenLaeufe';
import { Druckstapel } from '../../schreiben/Druckstapel';
import { ladeKorrektur, type Korrektur } from '../../../../lib/epost/korrektur';
import { briefKosten, euro, kostenUebersicht, ladePreise, type EpostPreise, type KostenBrief } from '../../../../lib/epost/kosten';

const ZEITRAUM = [{ key: '30', label: '30 Tage' }, { key: '90', label: '90 Tage' }, { key: '365', label: '1 Jahr' }, { key: 'alle', label: 'Alle' }] as const;
type ZeitKey = typeof ZEITRAUM[number]['key'];

const FARBE: Record<ReturnType<typeof stand>, { fg: string; bg: string; label: string }> = {
  unterwegs: { fg: 'var(--info)', bg: 'var(--info-soft)', label: 'Unterwegs' },
  versendet: { fg: 'var(--ok)', bg: 'var(--ok-soft)', label: 'Versendet' },
  fehler: { fg: 'var(--bad)', bg: 'var(--bad-soft)', label: 'Fehler' },
  wartet: { fg: 'var(--warn)', bg: 'var(--warn-soft)', label: 'Wartet (Sammelversand)' },
  abgebrochen: { fg: 'var(--ink3)', bg: 'var(--fill2)', label: 'Abgebrochen' },
  ersetzt: { fg: 'var(--ink3)', bg: 'var(--fill2)', label: 'Korrigiert neu gesendet' },
};

const datum = (iso: string) => new Date(iso).toLocaleString('de-DE', { day: '2-digit', month: '2-digit', year: '2-digit', hour: '2-digit', minute: '2-digit' });
const empfName = (b: AusgangBrief) => b.empfaenger?.zeilen?.[0] ?? '–';

/** Klickbarer Verweis (Empfänger, Objekt, Dokument, Akte) – ohne Ziel als einfacher Text. */
function Verweis({ icon: I, text, unter, pfad, onGehe }: { icon: Icon; text: string; unter?: string; pfad: string | null; onGehe: (p: string) => void }) {
  const inhalt = (
    <>
      <I size={14} weight="bold" style={{ flexShrink: 0 }} />
      <span className="truncate" style={{ fontWeight: 700 }}>{text}</span>
      {unter && <span style={{ color: 'var(--ink3)', fontWeight: 600 }}>{unter}</span>}
    </>
  );
  const stil: React.CSSProperties = { display: 'inline-flex', alignItems: 'center', gap: 6, maxWidth: '100%', minHeight: 32, padding: '0 10px', borderRadius: 10, fontSize: 12.5, border: 'none', fontFamily: 'inherit' };
  return pfad
    ? <button type="button" onClick={() => onGehe(pfad)} className="immo-hover-flaeche cursor-pointer" data-tip={`${text} öffnen`} style={{ ...stil, background: 'var(--fill2)', color: 'var(--acc)' }}>{inhalt}</button>
    : <span style={{ ...stil, background: 'var(--fill2)', color: 'var(--ink2)' }}>{inhalt}</span>;
}

function StandChip({ b }: { b: AusgangBrief }) {
  const s = stand(b);
  const f = FARBE[s];
  return (
    <span style={{ height: 24, padding: '0 10px', borderRadius: 12, display: 'inline-flex', alignItems: 'center', fontSize: 11.5, fontWeight: 800, background: f.bg, color: f.fg, whiteSpace: 'nowrap' }}>
      {s === 'unterwegs' ? statusText(b.status_id) : f.label}
    </span>
  );
}

export function PostausgangTab() {
  const navigate = useNavigate();
  const { mobil } = useMobilModus();
  const [briefe, setBriefe] = useState<AusgangBrief[] | null>(null);
  const [fehler, setFehler] = useState<string | null>(null);
  const [laedt, setLaedt] = useState(false);
  const [zeit, setZeit] = useState<ZeitKey>('90');
  const [standF, setStandF] = useState<StandFilter>('alle');
  const [mitTest, setMitTest] = useState(true);
  const [suche, setSuche] = useState('');
  const [offen, setOffen] = useState<Set<number>>(new Set());
  const [offenVorg, setOffenVorg] = useState<Set<string>>(new Set());
  const [neu, setNeu] = useState(false);
  const [meldung, setMeldung] = useState<{ ok: boolean; text: string } | null>(null);
  const [aktion, setAktion] = useState<string | null>(null);
  const [korrektur, setKorrektur] = useState<null | { brief: AusgangBrief; k: Korrektur }>(null);

  const laden = useCallback(async (abgleichen = false) => {
    setLaedt(true); setFehler(null);
    try {
      if (abgleichen) await aktualisiereBriefStatus().catch(() => undefined);
      setBriefe(await ladePostausgang(zeit === 'alle' ? null : Number(zeit)));
    } catch (e) { setFehler((e as Error).message); }
    finally { setLaedt(false); }
  }, [zeit]);
  useEffect(() => { void laden(); }, [laden]);
  // #586: Porto nur, wenn die Vertragspreise lesbar sind (Admins/Super Admin) und eingetragen
  const [preise, setPreise] = useState<EpostPreise | null>(null);
  useEffect(() => { void ladePreise().then((p) => setPreise(p.grund > 0 ? p : null), () => setPreise(null)); }, []);

  const basis = useMemo(() => (briefe ?? []).filter((b) => mitTest || !b.test), [briefe, mitTest]);
  const n = useMemo(() => zaehleStaende(basis), [basis]);
  const zeilen = useMemo(() => buendle(filtere(briefe ?? [], { stand: standF, mitTest, suche })), [briefe, standF, mitTest, suche]);
  const monate = useMemo(() => monatsUebersicht(briefe ?? []), [briefe]);
  const porto = useMemo(() => (preise && briefe ? kostenUebersicht(briefe as unknown as KostenBrief[], preise) : null), [preise, briefe]);
  const wartende = useMemo(() => basis.filter(wartetSammel).filter((b) => b.letter_id), [basis]);

  const testPdf = async (b: AusgangBrief) => {
    if (!b.letter_id) return;
    setAktion(b.id);
    try {
      const r = await ladeTestbrief(b.letter_id);
      if (!r.pdf_base64) throw new Error('Testbrief noch nicht verarbeitet – in ein paar Minuten erneut versuchen.');
      const bytes = Uint8Array.from(atob(r.pdf_base64), (c) => c.charCodeAt(0));
      window.open(URL.createObjectURL(new Blob([bytes], { type: 'application/pdf' })), '_blank', 'noopener');
    } catch (e) { setMeldung({ ok: false, text: (e as Error).message }); }
    finally { setAktion(null); }
  };

  const korrigieren = async (b: AusgangBrief) => {
    setAktion(b.id); setMeldung(null);
    try { setKorrektur({ brief: b, k: await ladeKorrektur(b) }); }
    catch (e) { setMeldung({ ok: false, text: (e as Error).message }); }
    finally { setAktion(null); }
  };

  const sammel = async (art: 'frei' | 'stop', liste: AusgangBrief[]) => {
    const ids = liste.map((b) => b.letter_id!).filter(Boolean);
    if (art === 'stop' && !window.confirm(`${ids.length} wartende ${ids.length === 1 ? 'Brief' : 'Briefe'} abbrechen? Sie werden nicht gedruckt.`)) return;
    setAktion(art); setMeldung(null);
    try {
      await (art === 'frei' ? sammelFreigeben(ids) : sammelAbbrechen(ids));
      setMeldung({ ok: true, text: art === 'frei' ? `${ids.length} sofort verschickt.` : `${ids.length} abgebrochen.` });
      await laden(art === 'frei');
    } catch (e) { setMeldung({ ok: false, text: (e as Error).message }); }
    finally { setAktion(null); }
  };

  const karte: React.CSSProperties = { borderRadius: 24, padding: mobil ? 14 : 18, gap: 12 };
  const titel: React.CSSProperties = { fontSize: 12, fontWeight: 800, letterSpacing: '0.05em', textTransform: 'uppercase', color: 'var(--ink3)' };

  const briefZeile = (b: AusgangBrief, eingerueckt = false, vorgaenger: AusgangBrief[] = []) => {
    const pfad = empfaengerPfad(b);
    const q = quelle(b);
    return (
      <li key={b.id} className="flex flex-col" style={{ gap: 4, padding: '10px 12px', borderRadius: 14, background: eingerueckt ? 'transparent' : 'var(--fill)', boxShadow: eingerueckt ? 'none' : 'inset 0 0 0 1px var(--line)' }}>
        <div className="flex flex-wrap items-center" style={{ gap: 8 }}>
          <span className="min-w-0 flex-1 truncate" style={{ fontSize: 13.5, fontWeight: 700 }}>
            {eingerueckt ? empfName(b) : (b.betreff || 'Brief')}
          </span>
          {b.test && <Chip groesse="xs">Test</Chip>}
          {b.einschreiben && <Chip groesse="xs">{b.einschreiben}</Chip>}
          <StandChip b={b} />
        </div>
        <div className="flex flex-wrap" style={{ gap: 6 }}>
          <Verweis icon={UserCircle} text={empfName(b)} unter={b.empfaenger_typ ? EMPF_ART[b.empfaenger_typ] : undefined} pfad={pfad} onGehe={navigate} />
          {b.objekt && <Verweis icon={Buildings} text={[b.objekt.objektnummer, b.objekt.bezeichnung].filter(Boolean).join(' ')} pfad={objektPfad(b)} onGehe={navigate} />}
          {q && <Verweis icon={FileText} text={q.label} pfad={q.pfad} onGehe={navigate} />}
          {b.ablage_datei_id && <Verweis icon={FolderOpen} text={b.objekt ? 'In der Akte' : 'Im Postausgang-Ordner'} pfad={ablagePfad(b)} onGehe={navigate} />}
        </div>
        <span className="immo-zahl" style={{ fontSize: 12, color: 'var(--ink3)' }}>
          {datum(b.created_at)}{b.seiten ? ` · ${b.seiten} S.` : ''}{preise && !b.test && b.status_id !== 99 && b.status_id !== -1 ? ` · Porto ${euro(briefKosten(b as unknown as KostenBrief, preise))}` : ''}
          {b.einschreiben_nr ? ` · ${b.einschreiben_nr}` : ''}{b.letter_id ? ` · Nr. ${b.letter_id}` : ''}
        </span>
        {b.status_id === 99 && b.fehler?.length ? <span style={{ fontSize: 12.5, color: 'var(--bad)' }}>{b.fehler.map((x) => [x.code, x.description].filter(Boolean).join(' ')).join('; ')}</span> : null}
        <div className="flex flex-wrap" style={{ gap: 6 }}>
          {b.status_id === 99 && !b.ersetzt_durch && (
            <ImmoButton groesse={32} art="akzent" icon={aktion === b.id ? SpinnerGap : PencilSimple} onClick={() => void korrigieren(b)} disabled={aktion !== null}>Korrigieren und neu senden</ImmoButton>
          )}
          {b.test && b.letter_id && <ImmoButton groesse={32} icon={aktion === b.id ? SpinnerGap : FilePdf} onClick={() => void testPdf(b)} disabled={aktion !== null}>Testbrief</ImmoButton>}
          {wartetSammel(b) && b.letter_id && (
            <>
              <ImmoButton groesse={32} icon={PaperPlaneTilt} onClick={() => void sammel('frei', [b])} disabled={aktion !== null}>Sofort verschicken</ImmoButton>
              <ImmoButton groesse={32} art="bad" icon={Stop} onClick={() => void sammel('stop', [b])} disabled={aktion !== null}>Abbrechen</ImmoButton>
            </>
          )}
        </div>
        {vorgaenger.length > 0 && (
          <div className="flex flex-col" style={{ gap: 4, marginTop: 2 }}>
            <button type="button" aria-expanded={offenVorg.has(b.id)} className="flex items-center text-left cursor-pointer"
              onClick={() => setOffenVorg((o) => { const x = new Set(o); if (x.has(b.id)) x.delete(b.id); else x.add(b.id); return x; })}
              style={{ gap: 6, border: 'none', background: 'transparent', color: 'var(--ink3)', padding: 0, fontSize: 12.5, fontWeight: 700, fontFamily: 'inherit' }}>
              {offenVorg.has(b.id) ? <CaretDown size={12} weight="bold" /> : <CaretRight size={12} weight="bold" />}
              {vorgaenger.length === 1 ? '1 frühere Fassung (abgelehnt, korrigiert)' : `${vorgaenger.length} frühere Fassungen (abgelehnt, korrigiert)`}
            </button>
            {offenVorg.has(b.id) && (
              <ul className="m-0 flex flex-col p-0" style={{ gap: 2, listStyle: 'none', paddingLeft: 16, borderLeft: '2px solid var(--line)' }}>
                {vorgaenger.map((v) => briefZeile(v, true))}
              </ul>
            )}
          </div>
        )}
      </li>
    );
  };

  return (
    <div className="flex flex-col min-w-0" style={{ gap: 14 }}>
      <section className="immo-glas flex flex-col min-w-0" style={karte}>
        <div className="flex flex-wrap items-center" style={{ gap: 10 }}>
          <span className="flex items-center justify-center" style={{ width: 40, height: 40, borderRadius: 12, background: 'var(--fill2)' }}><PostLogo size={24} /></span>
          <div className="flex min-w-0 flex-1 flex-col">
            <span style={{ fontSize: 17, fontWeight: 800 }}>Postausgang</span>
            <span style={{ fontSize: 12.5, color: 'var(--ink3)' }}>Alle Briefe, die per E-POST rausgingen – Stand wird stündlich abgefragt</span>
          </div>
          <ImmoButton icon={laedt ? SpinnerGap : ArrowClockwise} onClick={() => void laden(true)} disabled={laedt}>Stand abfragen</ImmoButton>
          <ImmoButton icon={PaperPlaneTilt} onClick={() => setNeu(true)}>Nur Brief</ImmoButton>
          <ImmoButton art="akzent" icon={PaperPlaneTilt} onClick={() => navigate('/admin/schreiben/neu')}>Neues Schreiben</ImmoButton>
        </div>
        <div className="grid" style={{ gridTemplateColumns: 'repeat(auto-fit, minmax(130px, 1fr))', gap: 8 }}>
          {(['unterwegs', 'wartet', 'fehler', 'versendet'] as const).map((k) => (
            <button key={k} type="button" onClick={() => setStandF(standF === k ? 'alle' : k)} className="flex flex-col text-left cursor-pointer"
              style={{ gap: 2, padding: '10px 12px', borderRadius: 16, border: 'none', background: FARBE[k].bg, color: 'var(--ink)', boxShadow: standF === k ? `inset 0 0 0 2px ${FARBE[k].fg}` : 'none' }}>
              <span className="immo-zahl" style={{ fontSize: 24, fontWeight: 800, color: FARBE[k].fg }}>{n[k]}</span>
              <span style={{ fontSize: 12, fontWeight: 700 }}>{FARBE[k].label}</span>
            </button>
          ))}
        </div>
        {wartende.length > 1 && (
          <div className="flex flex-wrap items-center" style={{ gap: 8, padding: '10px 12px', borderRadius: 14, background: 'var(--warn-soft)' }}>
            <span className="flex-1" style={{ fontSize: 13, fontWeight: 700 }}>{wartende.length} Briefe warten auf den Sammelversand (spätestens Freitag).</span>
            <ImmoButton groesse={32} icon={PaperPlaneTilt} onClick={() => void sammel('frei', wartende)} disabled={aktion !== null}>Alle sofort verschicken</ImmoButton>
            <ImmoButton groesse={32} art="bad" icon={Stop} onClick={() => void sammel('stop', wartende)} disabled={aktion !== null}>Alle abbrechen</ImmoButton>
          </div>
        )}
        {meldung && <span role={meldung.ok ? 'status' : 'alert'} style={{ fontSize: 13, fontWeight: 700, color: meldung.ok ? 'var(--ok)' : 'var(--bad)' }}>{meldung.text}</span>}
      </section>

      <section className="immo-glas flex flex-col min-w-0" style={karte}>
        <div className="flex flex-wrap items-center" style={{ gap: 10 }}>
          <div className="flex min-w-0 flex-1 items-center" style={{ gap: 8, height: 44, padding: '0 12px', borderRadius: 14, background: 'var(--fill2)', minWidth: 200 }}>
            <MagnifyingGlass size={15} color="var(--ink3)" />
            <input value={suche} onChange={(e) => setSuche(e.target.value)} placeholder="Empfänger, Betreff, Objekt, Nr. …" aria-label="Postausgang durchsuchen"
              className="min-w-0 flex-1" style={{ border: 'none', outline: 'none', background: 'transparent', color: 'var(--ink)', fontSize: 14, fontFamily: 'inherit' }} />
          </div>
          <Segment<ZeitKey> ariaLabel="Zeitraum" wert={zeit} onChange={setZeit} hoehe={mobil ? 44 : 34} eintraege={ZEITRAUM.map((z) => ({ key: z.key, label: z.label }))} />
        </div>
        <SchalterZeile klein an={mitTest} onChange={setMitTest} label="Testbriefe zeigen" />
        {fehler && <span role="alert" className="flex items-center" style={{ gap: 6, fontSize: 12.5, color: 'var(--bad)' }}><WarningCircle weight="fill" size={14} />{fehler}</span>}
        {briefe === null ? (
          <div className="animate-pulse" style={{ height: 160, borderRadius: 18, background: 'var(--fill2)' }} aria-busy="true" />
        ) : zeilen.length === 0 ? (
          <span style={{ fontSize: 13, color: 'var(--ink3)', padding: 8 }}>Keine Briefe für diese Auswahl.</span>
        ) : (
          <ul className="m-0 flex flex-col p-0" style={{ gap: 8, listStyle: 'none' }}>
            {zeilen.map((z) => {
              if (z.art === 'brief') return briefZeile(z.brief, false, z.vorgaenger);
              const st = zaehleStaende(z.briefe);
              const auf = offen.has(z.batchId);
              return (
                <li key={`s${z.batchId}`} className="flex flex-col" style={{ gap: 6, padding: '10px 12px', borderRadius: 14, background: 'var(--fill)', boxShadow: 'inset 0 0 0 1px var(--line)' }}>
                  <button type="button" aria-expanded={auf} onClick={() => setOffen((o) => { const x = new Set(o); if (x.has(z.batchId)) x.delete(z.batchId); else x.add(z.batchId); return x; })}
                    className="flex flex-wrap items-center text-left cursor-pointer" style={{ gap: 8, border: 'none', background: 'transparent', color: 'var(--ink)', padding: 0, fontFamily: 'inherit' }}>
                    {auf ? <CaretDown size={14} weight="bold" /> : <CaretRight size={14} weight="bold" />}
                    <span className="min-w-0 flex-1 truncate" style={{ fontSize: 13.5, fontWeight: 800 }}>Serienbrief · {z.betreff}</span>
                    <span className="immo-zahl" style={{ fontSize: 12.5, fontWeight: 700, color: 'var(--ink2)' }}>
                      {z.briefe.length} Briefe · {st.versendet} versendet{st.fehler ? ` · ${st.fehler} Fehler` : ''}{st.wartet ? ` · ${st.wartet} warten` : ''} · {datum(z.created_at)}
                    </span>
                  </button>
                  {auf && <ul className="m-0 flex flex-col p-0" style={{ gap: 2, listStyle: 'none', paddingLeft: 18 }}>{z.briefe.map((b) => briefZeile(b, true))}</ul>}
                </li>
              );
            })}
          </ul>
        )}
      </section>

      <Druckstapel karte={karte} titel={titel} />

      <section className="immo-glas flex flex-col min-w-0" style={karte} aria-label="Schreiben">
        <span style={titel}>Schreiben (E-Mail, Post, Portal und Druck aus dem Schreiben-Assistenten)</span>
        <SchreibenLaeufe />
      </section>

      <section className="immo-glas flex flex-col min-w-0" style={karte} aria-label="Monatsübersicht">
        <span style={titel}>Monatsübersicht (ohne Testbriefe) – passend zur Rechnung der Post</span>
        {porto ? (
          <>
            {porto.offen.briefe > 0 && <span style={{ fontSize: 12.5, color: 'var(--ink2)' }}>Noch nicht gedruckt: {porto.offen.briefe} Briefe · Porto ≈ {euro(porto.offen.summe)}</span>}
            {porto.monate.length === 0 && <span style={{ fontSize: 13, color: 'var(--ink3)' }}>Noch keine gedruckten echten Briefe im gewählten Zeitraum.</span>}
            {porto.monate.map((m) => (
              <div key={m.monat} className="flex flex-col" style={{ gap: 4, padding: '10px 12px', borderRadius: 14, background: 'var(--fill)' }}>
                <div className="flex flex-wrap items-baseline" style={{ gap: 10 }}>
                  <span style={{ fontSize: 14, fontWeight: 800 }}>{monatName(m.monat)}</span>
                  <span className="immo-zahl" style={{ fontSize: 13, color: 'var(--ink2)' }}>{m.briefe} Briefe · {m.blaetter} Blatt · Porto {euro(m.summe)}</span>
                </div>
                <span style={{ fontSize: 12.5, color: 'var(--ink3)' }}>{m.nachObjekt.map((o) => `${o.objekt}: ${o.briefe} · ${euro(o.summe)}`).join('  ·  ')}</span>
              </div>
            ))}
            <span style={{ fontSize: 12, color: 'var(--ink3)' }}>Porto nach den hinterlegten Vertragspreisen, je Objekt zum Umlegen. Gezählt nach Druckmonat wie auf der Rechnung der Post.</span>
          </>
        ) : monate.length === 0 ? (
          <span style={{ fontSize: 13, color: 'var(--ink3)' }}>Noch keine echten Briefe im gewählten Zeitraum.</span>
        ) : monate.map((m) => (
          <div key={m.monat} className="flex flex-col" style={{ gap: 4, padding: '10px 12px', borderRadius: 14, background: 'var(--fill)' }}>
            <div className="flex flex-wrap items-baseline" style={{ gap: 10 }}>
              <span style={{ fontSize: 14, fontWeight: 800 }}>{monatName(m.monat)}</span>
              <span className="immo-zahl" style={{ fontSize: 13, color: 'var(--ink2)' }}>{m.anzahl} Briefe · {m.seiten} Seiten{m.einschreiben ? ` · ${m.einschreiben} Einschreiben` : ''}</span>
            </div>
            <span style={{ fontSize: 12.5, color: 'var(--ink3)' }}>{m.nachObjekt.map((o) => `${o.objekt}: ${o.anzahl}`).join(' · ')}</span>
          </div>
        ))}
      </section>

      {korrektur && (
        <PostVersandDialog anlagen={korrektur.k.anlagen}
          start={{ treffer: korrektur.k.treffer, anschrift: korrektur.k.anschrift, betreff: korrektur.k.betreff, text: korrektur.k.text || undefined,
            mitAnschreiben: korrektur.k.mitAnschreiben, objektId: korrektur.k.objektId, optionen: korrektur.k.optionen, ersetzt: korrektur.brief.id, hinweis: korrektur.k.hinweis }}
          bezug={korrektur.brief.bezug_typ ? { typ: korrektur.brief.bezug_typ, id: korrektur.brief.bezug_id } : null}
          onSchliessen={() => setKorrektur(null)} onGesendet={(t) => { setKorrektur(null); setMeldung({ ok: true, text: `Korrigiert neu gesendet. ${t}` }); void laden(); }} />
      )}
      {neu && <PostVersandDialog anlagen={[]} start={{ mitAnschreiben: true }} onSchliessen={() => setNeu(false)} onGesendet={(t) => { setNeu(false); setMeldung({ ok: true, text: t }); void laden(); }} />}
    </div>
  );
}
